import React, { useCallback, useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import adminService from '../services/adminService';

const AdminSessionLogs = () => {
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    status: '',
    email: '',
    from: '',
    to: ''
  });
  const [dateFromInput, setDateFromInput] = useState('');
  const [dateToInput, setDateToInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const payload = {
        ...filters,
        email: filters.email.trim()
      };
      const data = await adminService.getSessionLogs(payload);
      setLogs(data.logs || []);
      setPagination(data.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to load session logs');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const setFilterValue = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key === 'page' ? value : 1
    }));
  };

  const toIsoDateTime = (value, endOfDay = false) => {
    if (!value) return '';
    const date = new Date(endOfDay ? `${value}T23:59:59.999` : `${value}T00:00:00.000`);
    return date.toISOString();
  };

  const handleApplyDateFilter = () => {
    setFilters((prev) => ({
      ...prev,
      from: dateFromInput ? toIsoDateTime(dateFromInput, false) : '',
      to: dateToInput ? toIsoDateTime(dateToInput, true) : '',
      page: 1
    }));
  };

  const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Session Logs</h1>
          <p className="mt-1 text-sm text-gray-600">
            Admin report of login activity, logout events, and abandoned sessions.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <select
              value={filters.status}
              onChange={(e) => setFilterValue('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All statuses</option>
              <option value="login_success">Login success</option>
              <option value="login_failed">Login failed</option>
              <option value="logout">Logout</option>
              <option value="abandoned">Abandoned</option>
            </select>

            <input
              type="email"
              placeholder="Filter by email"
              value={filters.email}
              onChange={(e) => setFilterValue('email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />

            <input
              type="date"
              value={dateFromInput}
              onChange={(e) => setDateFromInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />

            <input
              type="date"
              value={dateToInput}
              onChange={(e) => setDateToInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />

            <button
              onClick={handleApplyDateFilter}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
            >
              Apply date range
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-4 text-sm text-red-700">{error}</div>
        )}

        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">When</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Session</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-4 py-6 text-center text-gray-500">Loading session logs...</td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-6 text-center text-gray-500">No logs found.</td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log._id}>
                    <td className="px-4 py-3 text-sm text-gray-800">{formatDate(log.created_at)}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">{log.status}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">{log.email || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">{log.session_id || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">{log.ip_address || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">{log.reason || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.pages > 1 && (
          <div className="mt-4 flex items-center justify-between bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-700">
              Page {pagination.page} of {pagination.pages} ({pagination.total} logs)
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setFilterValue('page', pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setFilterValue('page', pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSessionLogs;
